import Head from 'next/head';
import { AnimatePresence, motion } from 'framer-motion';

import { getParkFromSlug, getAllParkSlugs } from '@/lib/api';
import {
  capitalize,
  formatPhoneNumber,
  slugify,
  cleanParkData,
} from '@/lib/util';
import Image from 'next/image';
import HeaderBanner from '@/components/HeaderBanner';
import ParkSubInfo from '@/components/ParkSubInfo';
import ThingsToDo from '@/components/ThingsToDo';
import Amenities from '@/components/Amenities';
import Activities from '@/components/Activities';
import Articles from '@/components/Articles';
import DisclosureItem from '@/components/DisclosureItem';
import Campgrounds from '@/components/Campgrounds';
import { useRouter } from 'next/router';
import LoaderSpinner from '@/components/LoaderSpinner';
import LoaderBar from '@/components/LoaderBar';
import useParkInfo from '@/components/useParkInfo';
import useScrollspy from '@/hooks/useScrollspy';
import { useEffect, useState } from 'react';
import TableOfContents from '@/components/TableOfContents';
import ParkItem from '@/components/ParkItem';
import { filterOutClosureEvents, getEventsAndSortByDate } from '@/lib/data';
import {
  getAllParks,
  getParkBySlug,
  getEventsByParkCode,
} from '@/lib/db-queries';
import EventItem from '@/components/EventItem';
import EventItemsGrid from '@/components/EventItemsGrid';
import ImageGallery from '@/components/ImageGallery';

function SectionTitle({ title, className }) {
  return (
    <h2
      id={slugify(title)}
      className={`text-2xl text-green-100 my-4 ${className}`}
    >
      <a
        href={`#${slugify(title)}`}
        className="hash-anchor"
        aria-label={`${title} anchor`}
      ></a>
      {title}
    </h2>
  );
}
export default function Park({ park, events }) {
  const router = useRouter();
  const slug = router.query.slug;

  const { data, isLoading } = useParkInfo({ slug });
  let amenities = data && data.amenities,
    campgrounds = data && data.campgrounds,
    alerts = data && data.alerts,
    articles = data && data.articles,
    thingsToDo = data && data.thingsToDo,
    loading;

  if (!data) loading = <LoaderBar bars={2} className={`mt-5`} />;

  // Transform ParkActivity relations to activities array format expected by Activities component
  const activities = park?.ParkActivity
      ? park.ParkActivity.map((pa) => pa.Activity)
      : park?.activities,
    url = park?.url,
    images = park?.images,
    firstImage = images?.[0] ? images?.[0] : '',
    fullName = park?.fullName,
    address = park?.addresses?.[0] ? park.addresses?.[0] : '',
    city = address?.city,
    state = address?.stateCode,
    designation = park?.designation,
    description = park?.description,
    weatherInfo = park?.weatherInfo,
    directions = park?.directionsUrl,
    operatingHours = park?.operatingHours,
    entranceFees = park?.entranceFees,
    entrancePasses = park?.entrancePasses,
    phoneNumber = park?.contacts?.phoneNumbers?.[0]
      ? park.contacts.phoneNumbers?.[0].phoneNumber
      : '';

  // Events are now passed as props from getStaticProps
  // console.log('events',events);
  // console.log('park', park)

  const { activeSection, handleProgrammaticScroll } = useScrollspy();

  const [width, setWidth] = useState(null);
  const [toc, setToc] = useState();
  const minLevel =
    toc?.reduce((min, item) => (item.level < min ? item.level : min), 10) ?? 0;
  const [isClient, setIsClient] = useState(false);

  useEffect(() => {
    setIsClient(true);
    const handleResize = () => setWidth(window.innerWidth);
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    const headings = document.querySelectorAll('main h1, main h2');

    const headingArr = [];
    headings.forEach((heading) => {
      const id = heading.id;
      const level = +heading.tagName.replace('H', '');
      const text = heading.textContent + '';

      headingArr.push({ id, level, text });
    });
    setToc(headingArr);
  }, [park, data]);

  // Only calculate isListView after client hydration to prevent mismatch
  const isListView = isClient && width !== null && width >= 768;

  return (
    <div className={`mx-auto`}>
      <Head>
        <title>{`${fullName || 'Park'} - Explore National Parks`}</title>
      </Head>

      <HeaderBanner image={images} alerts={alerts} />

      <main className="mx-auto max-w-8xl px-4 sm:px-6 md:px-8">
        <div
          className={`relative -mt-10 flex flex-wrap justify-between rounded bg-white max-md:p-6 md:p-10 max-w-8xl px-4 sm:px-6 md:px-8 pb-[55px] mx-auto`}
        >
          <div className="flex max-md:flex-col w-full justify-between">
            <div className="w-fit">
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.2 }}
                className="text-brown-100/50 text-sm"
              >
                {designation}
              </motion.p>
              <motion.h1
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-3xl font-normal leading-tight text-brown-200"
              >
                {fullName}
              </motion.h1>
            </div>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="text-xs mt-auto max-md:mt-5"
            >
              <a
                href={url}
                target="_blank"
                rel="noreferrer"
                className={`opacity-60 hover:opacity-100`}
              >
                visit on nps.gov{' '}
                <i className="fa-solid fa-up-right-from-square"></i>
              </a>
            </motion.div>
          </div>
        </div>

        <div
          className={`lg:grid lg:grid-cols-[auto,280px] lg:gap-6 max-md:mt-3 md:mt-6 mx-auto`}
        >
          <div className={`bg-white max-md:p-6 md:p-8 rounded`}>
            <div className={`relative flex justify-start items-center`}>
              <SectionTitle title="Info" className="sr-only" />

              {/*{images.length > 1 ?*/}
              {/*    <div className={`rounded-full relative h-[145px] w-[145px] flex justify-center items-center`}>*/}
              {/*        <i className="fa-solid fa-camera-retro absolute text-beige-200 text-xl z-1 opacity-50 hover:opacity-100"></i>*/}
              {/*        <div className="absolute top-0 left-0 h-full w-full bg-black opacity-20 rounded-full"></div>*/}
              {/*            <Image src={images[1].url} alt={images[1].altText} width="140" height="140" className={`h-[145px] w-[145px] rounded-full object-fill`} />*/}
              {/*    </div>*/}
              {/* : null}*/}

              {/*{phoneNumber ?*/}
              {/*    <ParkSubInfo title="Phone number">*/}
              {/*        <a href={`tel:${phoneNumber}`}>*/}
              {/*            {formatPhoneNumber(phoneNumber)}*/}
              {/*        </a>*/}
              {/*    </ParkSubInfo>*/}
              {/*    : null}*/}

              {/*<AnimatePresence>*/}
              {/*    {city && state ?*/}
              {/*        <ParkSubInfo title="Location">*/}
              {/*            {directions ?*/}
              {/*                <a href={directions} target="_blank" rel="noreferrer">*/}
              {/*                    {city}, {state}*/}
              {/*                </a>*/}
              {/*                : null}*/}
              {/*        </ParkSubInfo>*/}
              {/*        : null}*/}
              {/*</AnimatePresence>*/}
            </div>

            {/*{images.map((image) =>*/}
            {/*    <Image key={image.id} src={image.url} alt={image.altText} width="140" height="140" className={`h-auto w-[500px]`} />*/}
            {/*)}*/}

            {description ? <p>{description}</p> : null}

            {/* Park Photos Gallery */}
            {images && images.length > 0 && (
              <>
                <SectionTitle title="Park Photos" className="mt-8" />
                <ImageGallery
                  images={images}
                  parkImages={[]}
                  eventTitle={fullName}
                  showTitle={false}
                />
              </>
            )}

            {!data ? (
              <LoaderBar color={'beige-100'} bars={2} className={`mt-5`} />
            ) : null}
            {amenities && amenities?.length ? (
              <>
                <SectionTitle title="Amenities" className="mt-8" />
                <Amenities amenities={amenities} />
              </>
            ) : null}

            {activities?.length ? (
              <>
                <SectionTitle title="Activities" />
                <Activities activities={activities} />
              </>
            ) : null}

            {entranceFees?.length || entrancePasses?.length ? (
              <section>
                <div className={`grid grid-cols-1 gap-4 sm:grid-cols-2 mt-8`}>
                  {entranceFees?.length ? (
                    <div>
                      <SectionTitle title="Fees" />
                      {entranceFees.map((entranceFee, index) => (
                        <DisclosureItem
                          key={index}
                          button={`$${entranceFee.cost} - ${entranceFee.title}`}
                          panel={entranceFee.description}
                        />
                      ))}
                    </div>
                  ) : null}

                  {entrancePasses?.length ? (
                    <div>
                      <SectionTitle title="Passes" />
                      {entrancePasses.map((entrancePass, index) => (
                        <DisclosureItem
                          key={index}
                          button={`$${entrancePass.cost} - ${entrancePass.title}`}
                          panel={entrancePass.description}
                        />
                      ))}
                    </div>
                  ) : null}
                </div>
              </section>
            ) : null}

            {operatingHours?.length ? (
              <>
                <SectionTitle title="Operating Hours" className="mt-8" />
                <div>
                  {operatingHours.map((operatingHour, index) => (
                    <div
                      key={index}
                      className={`${index !== 0 ? 'mt-8' : ''} flow-root overflow-hidden`}
                    >
                      <p className="mb-3">{operatingHour.description}</p>
                      <table className="min-w-fit text-left text-sm">
                        <thead className="bg-white">
                          <tr className="bg-beige-100/30">
                            <th
                              scope="col"
                              className="relative isolate py-3.5 px-6"
                            >
                              Day
                            </th>
                            <th scope="col" className="px-6 py-3.5">
                              Hours
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(operatingHour.standardHours).map(
                            ([key, value]) => {
                              return (
                                <tr key={key} className="overflow-hidden">
                                  <td className="relative py-2 px-6">
                                    {capitalize(key)}
                                    <div className="absolute right-full bottom-0 h-px w-full bg-beige-100"></div>
                                    <div className="absolute bottom-0 left-0 h-px w-full bg-beige-100"></div>
                                  </td>
                                  <td className="relative py-2 px-6">
                                    {value}
                                    <div className="absolute right-full bottom-0 h-px w-full bg-beige-100"></div>
                                    <div className="absolute bottom-0 left-0 h-px w-full bg-beige-100"></div>
                                  </td>
                                </tr>
                              );
                            }
                          )}
                        </tbody>
                      </table>
                    </div>
                  ))}
                </div>
              </>
            ) : null}

            {weatherInfo ? (
              <>
                <SectionTitle title="Weather info" className="mt-8" />
                {weatherInfo}
              </>
            ) : null}

            {loading}
            {campgrounds && campgrounds?.length ? (
              <>
                <SectionTitle title={`Campgrounds`} className="mt-8" />
                <Campgrounds campgrounds={campgrounds} parkName={park.name} />
              </>
            ) : null}

            {loading}
            {thingsToDo && thingsToDo?.length ? (
              <>
                <SectionTitle
                  title={`Discover Things to Do`}
                  className="mt-8"
                />
                <ThingsToDo thingsToDo={thingsToDo} park={park} />
              </>
            ) : null}

            {loading}
            {articles && articles?.length ? (
              <>
                <SectionTitle title={`Articles`} className="mt-8" />
                <Articles articles={articles} park={park} />
              </>
            ) : null}

            {events && events?.length ? (
              <>
                <SectionTitle title={`Events`} className="mt-8" />
                <EventItemsGrid
                  events={events.slice(0, 10)}
                  isListView={isListView}
                  gridClassNames={'my-5 grid gap-6 my-5 grid-cols-1'}
                  headingClassNames="mt-10 text-4xl"
                  eventHeadingLevel={3}
                />
              </>
            ) : null}
          </div>

          <aside className="z-0 bg-white rounded py-4 px-8">
            <div className="sticky top-0 right-0">
              <TableOfContents
                toc={toc}
                minLevel={minLevel}
                activeSection={activeSection}
                onTOCClick={handleProgrammaticScroll}
              />
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
}

export async function getStaticProps({ params }) {
  try {
    const slug = params.slug;
    const park = await getParkBySlug(slug, true); // Include relations

    // Handle case where park is not found
    if (!park) {
      return {
        notFound: true,
      };
    }

    // Fetch events for this park
    const eventsData = await getEventsByParkCode(park.parkCode);
    let events = filterOutClosureEvents(eventsData);
    events = getEventsAndSortByDate(events);

    // Clean the data to remove undefined values that can't be serialized
    const cleanedPark = cleanParkData(park);
    const cleanedEvents = events.map((event) => cleanParkData(event));

    return {
      props: {
        park: cleanedPark,
        events: cleanedEvents,
      },
      revalidate: 86400, // Regenerate every 24 hours (data syncs weekly)
    };
  } catch (error) {
    console.error('Error in getStaticProps:', error);
    // Rethrow: a transient DB failure must not be cached by ISR as a permanent 404
    throw error;
  }
}

export async function getStaticPaths() {
  // Don't pre-render pages at build time - generate on-demand with ISR
  // This reduces build time from ~25 mins to ~2-3 mins
  return {
    paths: [],
    fallback: 'blocking',
  };
}
